import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  brouillon: "bg-secondary text-secondary-foreground",
  convoquee: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  vote_ouvert: "bg-majorelle/15 text-majorelle",
  cloturee: "bg-brand/10 text-brand dark:text-primary",
};

export default function Assemblies() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/assemblies"); setItems(data); }
    catch { toast.error(t("load_failed")); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-5" data-testid="assemblies-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icons.Gavel className="h-6 w-6 text-brand dark:text-primary shrink-0" />
          <h1 className="text-2xl font-bold truncate">{t("assemblies")}</h1>
        </div>
        {isRole("president") && (
          <button onClick={() => setCreating(true)} data-testid="ag-new-btn"
            className="h-10 px-4 rounded-xl bg-brand text-white font-bold text-sm flex items-center gap-1.5 active:scale-95 transition-transform shrink-0">
            <Icons.Plus className="h-4 w-4" /> {t("ag_new")}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl bg-card border border-border p-10 text-center text-muted-foreground" data-testid="ag-empty">
          <Icons.Gavel className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {t("ag_none")}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <button key={a.id} onClick={() => navigate(`/assemblies/${a.id}`)} data-testid={`ag-card-${a.id}`}
              className="w-full text-start rounded-2xl bg-card border border-border p-4 active:scale-[0.99] transition-transform">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[a.status] || ""}`}>{t("ag_status_" + a.status)}</span>
                <span className="text-xs text-muted-foreground">{t("ag_" + a.kind)}</span>
              </div>
              <p className="font-bold">{a.title}</p>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-4">
                <span className="flex items-center gap-1.5"><Icons.Calendar className="h-3.5 w-3.5" /> {fmtDate(a.scheduled_at)}</span>
                <span className="flex items-center gap-1.5"><Icons.ListChecks className="h-3.5 w-3.5" /> {a.agenda_count}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <CreateAGModal t={t} onClose={() => setCreating(false)} onCreated={(a) => { setCreating(false); navigate(`/assemblies/${a.id}`); }} />}
    </div>
  );
}

function CreateAGModal({ t, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", kind: "ordinaire", scheduled_at: "", location: "", quorum_pct: 50 });
  const [agenda, setAgenda] = useState([{ _k: crypto.randomUUID(), title: "", description: "", requires_vote: true }]);
  const [busy, setBusy] = useState(false);

  const setItem = (i, patch) => setAgenda((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setAgenda((prev) => [...prev, { _k: crypto.randomUUID(), title: "", description: "", requires_vote: true }]);
  const removeItem = (i) => setAgenda((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("ag_title_required")); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quorum_pct: Number(form.quorum_pct) || 50,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        agenda: agenda.filter((it) => it.title.trim()).map(({ _k, ...it }) => it),
      };
      const { data } = await api.post("/assemblies", payload);
      toast.success(t("ag_created"));
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="ag-create-modal">
      <div className="bg-card text-foreground rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{t("ag_new")}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("ag_title_label")} data-testid="ag-title" className={field} />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="ag-kind" className={field}>
              <option value="ordinaire">{t("ag_ordinaire")}</option>
              <option value="extraordinaire">{t("ag_extraordinaire")}</option>
            </select>
            <div className="relative">
              <input type="number" min="1" max="100" value={form.quorum_pct} onChange={(e) => setForm({ ...form, quorum_pct: e.target.value })} data-testid="ag-quorum" className={field} />
              <span className="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{t("ag_quorum_required")}</span>
            </div>
          </div>
          <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="ag-datetime" className={field} />
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t("ag_place")} data-testid="ag-place" className={field} />

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold">{t("ag_agenda")}</span>
              <button onClick={addItem} data-testid="ag-add-item" className="text-sm font-semibold text-majorelle flex items-center gap-1"><Icons.Plus className="h-4 w-4" /> {t("ag_add_item")}</button>
            </div>
            <div className="space-y-3">
              {agenda.map((it, i) => (
                <div key={it._k} className="rounded-xl border border-border p-3 space-y-2" data-testid={`ag-item-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-brand/10 text-brand dark:text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <input value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} placeholder={t("ag_item_title")} data-testid={`ag-item-title-${i}`} className="flex-1 h-10 rounded-lg border border-input bg-background px-3 outline-none text-sm" />
                    {agenda.length > 1 && (
                      <button onClick={() => removeItem(i)} data-testid={`ag-item-remove-${i}`} className="h-8 w-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0"><Icons.Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                  <input value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder={t("ag_item_desc")} className="w-full h-9 rounded-lg border border-input bg-background px-3 outline-none text-sm" />
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={it.requires_vote} onChange={(e) => setItem(i, { requires_vote: e.target.checked })} data-testid={`ag-item-vote-${i}`} className="h-4 w-4 accent-[var(--brand,#0F5132)]" />
                    {t("ag_requires_vote")}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <button onClick={submit} disabled={busy} data-testid="ag-create-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? "…" : t("ag_create")}</button>
        </div>
      </div>
    </div>
  );
}
